import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudFog, CloudLightning,
  CloudDrizzle, Snowflake, Loader2, MapPin,
} from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

const DOW = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

function codeInfo(c) {
  if (c === 0) return { Icon: Sun, label: "Sereno" };
  if (c === 1 || c === 2) return { Icon: CloudSun, label: "Poco nuvoloso" };
  if (c === 3) return { Icon: Cloud, label: "Nuvoloso" };
  if (c === 45 || c === 48) return { Icon: CloudFog, label: "Nebbia" };
  if (c >= 51 && c <= 57) return { Icon: CloudDrizzle, label: "Pioviggine" };
  if (c >= 61 && c <= 67) return { Icon: CloudRain, label: "Pioggia" };
  if (c >= 71 && c <= 77) return { Icon: CloudSnow, label: "Neve" };
  if (c >= 80 && c <= 82) return { Icon: CloudRain, label: "Rovesci" };
  if (c === 85 || c === 86) return { Icon: CloudSnow, label: "Rovesci di neve" };
  if (c >= 95) return { Icon: CloudLightning, label: "Temporale" };
  return { Icon: Cloud, label: "—" };
}

const fmtDow = (iso) => {
  try { return DOW[new Date(iso + "T00:00:00").getDay()]; } catch { return ""; }
};
const fmtDay = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); } catch { return iso; }
};

export default function WeatherWidget() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/weather").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return null;

  return (
    <section id="meteo" className="section-pad px-6 md:px-16 bg-fir" data-testid="weather-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-sm mb-2">{t("Meteo · 7 giorni")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-ivory">{t("Che tempo fa in Val di Sole")}</h2>
          </div>
          {data?.current?.temp != null && (
            <div className="flex items-center gap-3 text-ivory/90" data-testid="weather-current">
              {(() => { const { Icon } = codeInfo(data.current.code); return <Icon className="text-gold" size={34} />; })()}
              <span className="font-display text-3xl">{Math.round(data.current.temp)}°</span>
              <span className="flex items-center gap-1 text-sm text-ivory/60"><MapPin size={14} /> Dimaro</span>
            </div>
          )}
        </div>

        {!data ? (
          <div className="flex items-center gap-2 text-ivory/70 py-10 justify-center" data-testid="weather-loading">
            <Loader2 className="animate-spin" size={20} /> {t("Caricamento meteo…")}
          </div>
        ) : (
          <>
            {data.is_winter && (
              <div data-testid="weather-snow-banner" className="mb-6 rounded-2xl border border-gold/30 bg-gold/10 px-5 py-4 flex flex-wrap items-center gap-3">
                <Snowflake className="text-gold shrink-0" size={22} />
                <p className="text-ivory text-sm md:text-base">
                  <span className="font-semibold">{t("Neve prevista")}:</span>{" "}
                  {data.snow_total_week > 0
                    ? <>{t("circa")} <b className="text-gold">{data.snow_total_week} cm</b> {t("nei prossimi 7 giorni")}</>
                    : t("nessuna nevicata significativa nei prossimi 7 giorni")}
                  {data.current?.snow_depth_cm > 0 && (
                    <span className="text-ivory/70"> · {t("neve al suolo")} ~{data.current.snow_depth_cm} cm</span>
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="weather-days">
              {data.days.map((d, i) => {
                const { Icon, label } = codeInfo(d.code);
                return (
                  <motion.div key={d.date} data-testid={`weather-day-${i}`}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="rounded-2xl bg-ivory/5 border border-ivory/10 p-4 text-center text-ivory hover:bg-ivory/10 transition-colors">
                    <div className="text-xs uppercase tracking-wide text-gold">{i === 0 ? t("Oggi") : t(fmtDow(d.date))}</div>
                    <div className="text-[11px] text-ivory/50 mb-2">{fmtDay(d.date)}</div>
                    <Icon className="mx-auto text-ivory/90" size={30} />
                    <div className="text-[11px] text-ivory/60 mt-1 h-8 flex items-center justify-center leading-tight">{t(label)}</div>
                    <div className="mt-1 text-sm">
                      <span className="font-semibold">{Math.round(d.tmax)}°</span>
                      <span className="text-ivory/50"> / {Math.round(d.tmin)}°</span>
                    </div>
                    {d.snow > 0 ? (
                      <div className="mt-1.5 text-[11px] text-gold flex items-center justify-center gap-1"><Snowflake size={11} /> {d.snow} cm</div>
                    ) : d.rain_prob != null ? (
                      <div className="mt-1.5 text-[11px] text-ivory/45 flex items-center justify-center gap-1"><CloudRain size={11} /> {d.rain_prob}%</div>
                    ) : <div className="mt-1.5 h-4" />}
                  </motion.div>
                );
              })}
            </div>
            <p className="text-ivory/40 text-xs mt-4 text-right">{t("Dati meteo forniti da Open-Meteo")}</p>
          </>
        )}
      </div>
    </section>
  );
}
